<template>
    <PageWrapper
        :loading="isLoading"
        title="API & Webhooks"
        :description="description"
    >
        <ApiKeySection
            :account-name="apiKeyStatus.data.accountName"
            :api-key="apiKeyStatus.data.key"
        />
        <WebhooksSection
            v-if="webhookTypesStatus.list.length > 0"
            class="mt-6"
            :webhooks="webhooksStatus.list"
            :webhook-types="webhookTypesStatus.list"
            :deleting="deletingWebhook"
            :updating="updatingWebhook"
            @change="updateWebhook"
            @delete="deleteWebhook"
        />
    </PageWrapper>
</template>

<style lang="scss">
.ApiSettings {
    // TODO[PRODUCT-20869]: remove temp solution unblocks evie from view content once long term solution provided
    .PageWrapper {
        padding-bottom: 100px;
    }
}
</style>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { InsuranceApiKey, InsuranceWebhook } from '@evidentid/tprm-portal-lib/models/api-settings';
    import {
        InsuranceWebhookType,
    } from '@evidentid/tprm-portal-lib/models/api-settings/InsuranceWebhookType.model';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import ApiKeySection from '@/modules/api-settings/components/api-key-section/ApiKeySection.vue';
    import WebhooksSection from '@/modules/api-settings/components/webhooks-section/WebhooksSection.vue';
    import { useApiSettingsStore } from '@/modules/api-settings/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    @Component({
        components: { ApiKeySection, WebhooksSection, PageWrapper },
    })
    export default class ApiSettings extends Vue {
        private get apiSettingsStore() {
            return useApiSettingsStore();
        }

        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private description = 'Access your API key and configure webhooks to connect Evident with your internal systems and automate data sharing.';

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
        }

        @Watch('isLoading', { immediate: true })
        private onLoadingChange(): void {
            this.pageLayoutStore.setLoading(this.isLoading);
        }

        private get isLoading(): boolean {
            return this.apiKeyStatus.status === OperationStatus.loading ||
                this.webhooksStatus.status === OperationStatus.loading ||
                this.webhookTypesStatus.status === OperationStatus.loading;
        }

        private get rpName(): string {
            return this.$rp.current!;
        }

        private get apiKeyStatus(): { status: OperationStatus, data: InsuranceApiKey } {
            return this.apiSettingsStore.apiKey[this.rpName] || {
                status: OperationStatus.uninitialized,
                data: {},
            };
        }

        private get webhooksStatus(): { status: OperationStatus, list: InsuranceWebhook[] } {
            return this.apiSettingsStore.webhooks[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get webhookTypesStatus(): { status: OperationStatus, list: InsuranceWebhookType[] } {
            return this.apiSettingsStore.webhookTypes[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get updateWebhookStatus(): OperationStatus {
            return this.apiSettingsStore.patchWebhookStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get createWebhookStatus(): OperationStatus {
            return this.apiSettingsStore.createWebhookStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get deleteWebhookStatus(): OperationStatus {
            return this.apiSettingsStore.deleteWebhookStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get updatingWebhook(): boolean {
            return this.updateWebhookStatus === OperationStatus.loading ||
                this.createWebhookStatus === OperationStatus.loading;
        }

        private get deletingWebhook(): boolean {
            return this.deleteWebhookStatus === OperationStatus.loading;
        }

        @Watch('$rp.current', { immediate: true })
        private async onRpChanges(newVal: string, oldVal: string): Promise<void> {
            if (newVal && newVal !== oldVal) {
                await useRbacStore().validateRbac([ RbacFeature.configurationApiSettings ]);
            }
            this.loadApiKey();
            this.loadWebhooks();
            this.loadWebhookTypes();
        }

        private loadApiKey() {
            if (this.apiKeyStatus.status !== OperationStatus.loading) {
                this.apiSettingsStore.loadApiKey({ rpName: this.rpName });
            }
        }

        private loadWebhooks() {
            if (this.webhooksStatus.status !== OperationStatus.loading) {
                this.apiSettingsStore.loadWebhooks({ rpName: this.rpName });
            }
        }

        private loadWebhookTypes() {
            if (this.webhookTypesStatus.status !== OperationStatus.loading) {
                this.apiSettingsStore.loadWebhookTypes({ rpName: this.rpName });
            }
        }

        private async updateWebhook(webhook: InsuranceWebhook): Promise<void> {
            if (this.updateWebhookStatus !== OperationStatus.loading) {
                if (webhook.id) {
                    await this.apiSettingsStore.patchWebhook({ rpName: this.rpName, data: webhook });
                } else {
                    await this.apiSettingsStore.createWebhook({ rpName: this.rpName, data: webhook });
                }
            }
        }

        private async deleteWebhook(webhook: InsuranceWebhook): Promise<void> {
            if (this.deleteWebhookStatus !== OperationStatus.loading) {
                await this.apiSettingsStore.deleteWebhook({ rpName: this.rpName, data: webhook });
            }
        }

        private unmounted() {
            this.apiSettingsStore.clearApiKey({ rpName: this.rpName });
            this.apiSettingsStore.clearWebhooks({ rpName: this.rpName });
            this.apiSettingsStore.clearWebhookTypes({ rpName: this.rpName });
            this.apiSettingsStore.clearCreatingWebhookStatus({ rpName: this.rpName });
            this.apiSettingsStore.clearPatchingWebhookStatus({ rpName: this.rpName });
            this.apiSettingsStore.clearDeletingWebhookStatus({ rpName: this.rpName });
        }
    }
</script>
